import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api/client';

/** Stores the raw 30-day keyboard JWT (not an access token) for Tier-2 re-use. */
const CLOUD_STORAGE_KB_KEY = 'swKbToken';

/** Upper bound for a whole authentication run before the UI switches to an error state. */
export const AUTH_TIMEOUT_MS = 10000;

/** Write a value to Telegram CloudStorage if available (Bot API 6.9+). */
function cloudStorageSet(key: string, value: string): void {
    try {
        window.Telegram?.WebApp?.CloudStorage?.setItem(key, value);
    } catch {
        // CloudStorage not available — ignore silently
    }
}

/** Remove a value from Telegram CloudStorage if available. */
function cloudStorageRemove(key: string): void {
    try {
        window.Telegram?.WebApp?.CloudStorage?.removeItem(key);
    } catch {
        // CloudStorage not available — ignore silently
    }
}

/** Read a value from Telegram CloudStorage. Returns null if unavailable or not found, with a 2s safety timeout. */
function cloudStorageGet(key: string): Promise<string | null> {
    return new Promise((resolve) => {
        let settled = false;
        const timeout = setTimeout(() => {
            if (!settled) {
                settled = true;
                resolve(null);
            }
        }, 2000);

        try {
            const cs = window.Telegram?.WebApp?.CloudStorage;
            if (!cs) {
                clearTimeout(timeout);
                resolve(null);
                return;
            }
            cs.getItem(key, (err: Error | null, value?: string) => {
                if (!settled) {
                    settled = true;
                    clearTimeout(timeout);
                    resolve(err ? null : (value ?? null));
                }
            });
        } catch {
            if (!settled) {
                settled = true;
                clearTimeout(timeout);
                resolve(null);
            }
        }
    });
}

/**
 * Hook to handle Telegram Mini App authentication.
 *
 * Authentication strategy (3-tier fallback for keyboard button Mini Apps):
 *
 * Tier 1 — initData (inline button): standard path, always works, sets CloudStorage token.
 * Tier 2 — CloudStorage token: persisted after a Tier-1 auth; used when initData is empty.
 * Tier 3 — JWT in URL (?kt=…): 30-day signed token embedded by the bot in the keyboard URL.
 *           Used when CloudStorage token is absent/expired. On success, writes to CloudStorage.
 * Tier 4 — keyboard refresh: if both Tier 2 and Tier 3 fail, asks the backend
 *           (POST tg/request-refresh) to send a fresh keyboard to the chat. Only if that
 *           fails, falls back to sendData('refresh:<subId>') so the bot is not asked twice.
 */
export const useTelegramAuth = () => {
    const { user, loadUser, isAuthenticated } = useAuth();
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [hasAttempted, setHasAttempted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [subscription, setSubscription] = useState<any>(null);
    /**
     * Incremented on every run start and on timeout. A run only applies its results while
     * its id is still current, so a request finishing after the timeout cannot overwrite state.
     */
    const runIdRef = useRef(0);

    /** Tier 1: Standard initData authentication (inline button). */
    const authenticate = useCallback(async () => {
        const tg = window.Telegram?.WebApp;
        if (!tg || !tg.initData) {
            setError('Not running inside Telegram or missing initData');
            return;
        }
        if (isAuthenticating || hasAttempted) return;

        const runId = ++runIdRef.current;
        const isCurrent = () => runIdRef.current === runId;
        setIsAuthenticating(true);
        setError(null);

        try {
            const urlParams = new URLSearchParams(window.location.search);
            const startParam = urlParams.get('tgWebAppStartParam') || urlParams.get('startapp') || undefined;

            const response = await api.post('tg/auth', { initData: tg.initData, startParam });
            if (!isCurrent()) return;
            const data = response.data;
            setSubscription(data.subscription);
            await loadUser();
            console.log('TWA Authentication successful (initData)', data.user);
            if (isCurrent()) setHasAttempted(true);
        } catch (err: any) {
            if (!isCurrent()) return;
            console.error('TWA Authentication failed', err);
            setError(err.response?.data?.message || err.message || 'Authentication failed');
            setHasAttempted(true);
        } finally {
            if (isCurrent()) setIsAuthenticating(false);
        }
    }, [loadUser, isAuthenticating, hasAttempted]);

    /** Tier 2+3: Keyboard button path — no initData available. */
    const authenticateKeyboard = useCallback(async () => {
        if (isAuthenticating || hasAttempted) return;
        const runId = ++runIdRef.current;
        const isCurrent = () => runIdRef.current === runId;
        setIsAuthenticating(true);
        setError(null);

        const urlParams = new URLSearchParams(window.location.search);
        const subId = urlParams.get('startapp') || urlParams.get('tgWebAppStartParam');

        try {
            // --- Tier 2: CloudStorage keyboard JWT ---
            // Only use the dedicated kb-token key; never use the session access token
            const storedKbToken = await cloudStorageGet(CLOUD_STORAGE_KB_KEY);
            if (storedKbToken) {
                try {
                    const response = await api.post('tg/auth-keyboard', { keyboardToken: storedKbToken });
                    if (!isCurrent()) return;
                    const data = response.data;
                    setSubscription(data.subscription);
                    await loadUser();
                    console.log('TWA Authentication successful (CloudStorage kb token)', data.user);
                    setHasAttempted(true);
                    return;
                } catch (err: any) {
                    // Expired or invalid — purge from CloudStorage and fall through to Tier 3.
                    // Keep the token on network/server errors; it may still be valid.
                    if (err?.response?.status === 401) {
                        cloudStorageRemove(CLOUD_STORAGE_KB_KEY);
                    }
                    console.warn('CloudStorage kb token rejected, trying URL token', err?.response?.data?.error);
                }
            }

            // --- Tier 3: JWT in URL (?kt=…) ---
            const kt = urlParams.get('kt');
            if (kt) {
                try {
                    const response = await api.post('tg/auth-keyboard', { keyboardToken: kt });
                    if (!isCurrent()) return;
                    const data = response.data;
                    setSubscription(data.subscription);
                    await loadUser();
                    // Cache the raw keyboard JWT (not the session token) so Tier 2 works next time
                    cloudStorageSet(CLOUD_STORAGE_KB_KEY, kt);
                    console.log('TWA Authentication successful (URL kb token)', data.user);
                    setHasAttempted(true);
                    return;
                } catch (err: any) {
                    console.warn('URL keyboard token rejected, triggering refresh', err?.response?.data?.error);
                }
            }

            if (!isCurrent()) return;

            // --- Tier 4: Server request-refresh, sendData only as fallback ---
            let refreshSent = false;
            const tokenToRefresh = kt || storedKbToken;
            if (tokenToRefresh) {
                try {
                    console.info('Requesting keyboard token refresh from server...');
                    const refreshResp = await api.post('tg/request-refresh', { keyboardToken: tokenToRefresh });
                    if (refreshResp.data?.ok) {
                        refreshSent = true;
                    }
                } catch (refreshErr) {
                    console.warn('Server request-refresh failed, trying sendData', refreshErr);
                }
            }
            if (!isCurrent()) return;

            // Fallback only: if the server already sent a fresh keyboard, sendData would
            // make the bot send the same message a second time (handleWebAppData).
            const tg = window.Telegram?.WebApp;
            if (!refreshSent && tg?.sendData && subId) {
                try {
                    console.info('Triggering keyboard refresh via sendData');
                    tg.sendData(`refresh:${subId}`);
                    refreshSent = true;
                } catch (sendErr) {
                    console.warn('sendData failed:', sendErr);
                }
            }

            setHasAttempted(true);

            if (refreshSent) {
                setError('TOKEN_EXPIRED_REFRESHED');
                return;
            }

            // No recovery possible — show guidance
            setError('Öffne die App bitte über die angeheftete Nachricht in diesem Chat, um dich einzuloggen.');
        } catch (err: any) {
            if (!isCurrent()) return;
            console.error('Keyboard authentication failed', err);
            setError(err.response?.data?.message || err.message || 'Authentication failed');
            setHasAttempted(true);
        } finally {
            if (isCurrent()) setIsAuthenticating(false);
        }
    }, [loadUser, isAuthenticating, hasAttempted]);

    useEffect(() => {
        if (hasAttempted) return;
        if (window.Telegram?.WebApp?.initData) {
            // Tier 1: initData present (inline button or menu button launch)
            authenticate();
        } else {
            // Keyboard button launch: initData is always empty by Telegram design
            authenticateKeyboard();
        }
    }, [authenticate, authenticateKeyboard, hasAttempted]);

    // Safety timeout: Ensure isAuthenticating never hangs indefinitely (e.g. stalled network or SDK)
    useEffect(() => {
        if (!isAuthenticating) return;
        const timer = setTimeout(() => {
            console.warn(`Authentication timed out after ${AUTH_TIMEOUT_MS / 1000}s`);
            // Invalidate the running attempt so late responses are ignored
            runIdRef.current++;
            setError('Ladezeit überschritten. Bitte versuche es erneut.');
            setHasAttempted(true);
            setIsAuthenticating(false);
        }, AUTH_TIMEOUT_MS);

        return () => clearTimeout(timer);
    }, [isAuthenticating]);

    const retry = useCallback(() => {
        setHasAttempted(false);
    }, []);

    return {
        user,
        subscription,
        isAuthenticating,
        isAuthenticated,
        hasAttempted,
        error,
        retry,
    };
};
